/**
 * src/lib/voice/enqueueWithBreadcrumb.js — atomic voice intake helper.
 *
 * TICKET-VOICE-QUEUE-PHANTOM-RESIDUAL (2026-05-06).
 *
 * Background. `useVoiceBreadcrumbs` listens for `voice:record_stop` and
 * inserts an optimistic "Processing..." row keyed on the event detail's
 * `recordingId`. Later, when the server row arrives via realtime, the
 * reconciler matches `optimisticRow.idempotency_key` against
 * `voice_records.idempotency_key` and removes the optimistic row.
 *
 * The previous fix (commit 7374006) made `enqueueVoiceSession` ACCEPT
 * `recording_id` — but `useEntityMic` was never updated to PASS it, so
 * the receiver auto-generated a fresh UUID and the two ids never matched.
 * The optimistic card lived forever, exactly the white "Processing... /
 * Transcribing audio..." phantom CEO reported on every mic recording.
 *
 * This helper makes the bug impossible by construction: one call, one id,
 * emit BEFORE enqueue. Any new mic recorder hook should use this helper
 * instead of calling `emitVoiceEvent('voice:record_stop', ...)` and
 * `enqueueVoiceSession({...})` separately.
 *
 * For chunked recorders that bypass `enqueueVoiceSession` entirely
 * (`useChunkedRecorder`, `useWhisperRecording` — they upload through
 * `uploadChunk` which already threads `recording_id` end-to-end via
 * `lib/handlers/voice-v2.js findOrCreateVoiceRecord`), this helper is
 * not applicable. They are already correct.
 */
import { emitVoiceEvent } from '../voiceEvents';
import { enqueueVoiceSession } from './enqueue';
import { PROJECT_UPLOAD_SCOPES } from './voiceScopes';

function newRecordingId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `rec-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * Atomically emit `voice:record_stop` and enqueue a recording for IDB +
 * server processing, with one shared `recording_id`.
 *
 * @param {object} args
 * @param {Blob}   args.blob          — required, the captured audio
 * @param {string} args.contextType   — required, scope type ('project' | 'task' | 'deal' | 'bundle' | 'general' | …);
 *                                       used both for the event detail
 *                                       (so `useVoiceBreadcrumbs.contextMatches` filters correctly)
 *                                       AND mapped to `scope_type` for the receiver.
 * @param {string|null} args.contextId — scope id; mapped to `scope_id`.
 * @param {string} [args.scopeLabel]  — human label for the tray banner; forwarded to receiver.
 * @param {string} [args.fileName]    — file_name for IDB row.
 * @param {string} [args.language]    — optional Whisper language hint.
 * @param {string} [args.contextHint] — optional classifier hint (e.g. 'procurement',
 *                                       pipeline/task/profile override). Forwarded as
 *                                       `context_hint` on the IDB row so voiceProcessFn
 *                                       can pass it to the backend classifier.
 * @param {string} [args.recording_id] — caller-supplied id (preferred when the
 *                                       caller wants control of the prefix, e.g. `entity-…`,
 *                                       `chunked-…`). If omitted, the helper generates one.
 * @returns {Promise<{ recording_id: string }>} — the shared id, for any
 *          downstream callers that need to correlate.
 */
export async function enqueueWithBreadcrumb({
  blob,
  contextType,
  contextId = null,
  scopeLabel,
  fileName,
  language,
  contextHint,
  recording_id: callerRecordingId,
  forceEmit = false,
  duration_sec,
} = {}) {
  if (!blob) {
    throw new Error('[enqueueWithBreadcrumb] blob is required');
  }
  if (!contextType) {
    throw new Error('[enqueueWithBreadcrumb] contextType is required');
  }

  const recordingId = callerRecordingId || newRecordingId();

  // TICKET-VOICE-PHANTOM-PROJECT (CEO live test 2026-05-06 evening):
  // For project-upload scopes the cron drainer's voice_records insert leaves
  // idempotency_key=NULL (no client-known id is threaded through
  // uploadVoiceFile → voice_uploads → kickClassification → voice_records).
  // The optimistic row in useVoiceBreadcrumbs is keyed on `entity-...` and
  // reconciliation requires both keys to be non-null and equal — so the
  // optimistic card lives forever, recreating the phantom-card symptom now
  // that voice_transcripts is correctly written and the real card visually
  // distinct. Project recordings already get in-flight UI in the
  // VoiceQueueTray; emitting voice:record_stop for them adds a duplicate
  // card with no path to clear. Skip the emit for project-upload scopes.
  // In-app scopes (task / deal / bundle / general) keep the emit because
  // their server path threads recording_id end-to-end via uploadChunk →
  // findOrCreateVoiceRecord (`lib/handlers/voice-v2.js:994`) and the
  // optimistic row reconciles correctly.
  //
  // Bug 1 (CEO 2026-05-07): forceEmit=true is the escape hatch for callers
  // that DO thread recording_id end-to-end (VoiceFileUploadBtn -> uploadVoiceFile
  // with idempotency_key -> voice_uploads.idempotency_key -> kickClassification
  // -> voice_records.idempotency_key). When threading is in place the
  // optimistic row reconciles correctly even on project-upload scope; the
  // PROJECT_UPLOAD_SCOPES skip is the wrong default for those callers.
  const shouldEmitOptimistic = forceEmit || !PROJECT_UPLOAD_SCOPES.has(contextType);

  if (shouldEmitOptimistic) {
    // Emit BEFORE enqueue. `useVoiceBreadcrumbs.js` listens synchronously
    // and inserts the optimistic row; by the time the IDB write lands and
    // the worker drains to the server, the optimistic row is already in
    // place keyed on the same id that `voice_records.idempotency_key` will
    // be set to. Reconciliation fires on the very next realtime tick.
    emitVoiceEvent('voice:record_stop', {
      contextType,
      contextId,
      recordingId,
    });
  }

  await enqueueVoiceSession({
    blob,
    scope_type: contextType,
    scope_id: contextId,
    scope_label: scopeLabel,
    file_name: fileName,
    language,
    recording_id: recordingId,
    // TICKET-064: forward MediaRecorder duration through to IDB row.
    duration_sec,
    // TICKET-CODEX-3172673721: forward contextHint so voiceProcessFn reads
    // rec.context_hint and passes it to the backend classifier.
    context_hint: contextHint ?? undefined,
  });

  return { recording_id: recordingId };
}
